import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import Icon from "@/components/Icon"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { supabase } from "@/lib/supabase"
import { getDefaultHomePageForAccessToken } from "@/utils/roleUtils"

const KNOWN_OTP_TYPES = ["invite", "recovery", "magiclink", "email_change", "signup"] as const
type EmailOtpType = (typeof KNOWN_OTP_TYPES)[number]

const isEmailOtpType = (value: string): value is EmailOtpType =>
  (KNOWN_OTP_TYPES as readonly string[]).includes(value)

function getErrorMessage(e: unknown): string {
  if (e instanceof Error) return e.message
  if (typeof e === "string") return e
  try {
    return JSON.stringify(e)
  } catch {
    return "An unknown error occurred"
  }
}

const parseHashParams = (hash: string) => {
  const params = new URLSearchParams()
  if (hash?.startsWith("#")) {
    const pairs = hash.slice(1).split("&")
    for (const p of pairs) {
      const [k, v] = p.split("=")
      if (k) params.set(k, decodeURIComponent(v || ""))
    }
  }
  return params
}

const ActivateUser: React.FC = () => {
  const navigate = useNavigate()

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), [])
  const hashParams = useMemo(() => parseHashParams(window.location.hash), [])

  const code = searchParams.get("code") || hashParams.get("code")
  const tokenHash = searchParams.get("token_hash") || hashParams.get("token_hash")
  const type = (searchParams.get("type") || hashParams.get("type") || "").toLowerCase()
  const otpType: EmailOtpType = isEmailOtpType(type) ? type : "invite"
  const isRecovery = type === "recovery"

  // A token_hash link isn't verified until the form is submitted: email scanners that open links
  // (and some run the page's JS) would otherwise spend the single-use token before the user clicks.
  // So it goes straight to the form, while the other link types are verified on load.
  const [verifying, setVerifying] = useState(!tokenHash)
  const [verified, setVerified] = useState(!!tokenHash)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [submitting, setSubmitting] = useState(false)
  // A token hash works once. Remembered so a retry after a failed password update doesn't spend it again.
  const tokenHashVerified = useRef(false)

  const performVerification = useCallback(async () => {
    try {
      // If OAuth/code exchange flow
      if (code) {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code)
        if (error) throw error
        setVerified(true)
        setEmail(data.user?.email || "")
        return
      }

      // Some links return access_token directly in hash
      const accessToken = hashParams.get("access_token")
      const refreshToken = hashParams.get("refresh_token")
      if (accessToken && refreshToken) {
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        })
        if (error) throw error
        setVerified(true)
        setEmail(data.user?.email || "")
        return
      }

      // Nothing to verify
      toast.error("Invalid or expired activation link.")
      navigate("/login", { replace: true })
    } catch (err: unknown) {
      console.error("Activation verification failed:", err)
      toast.error(getErrorMessage(err) || "Failed to verify activation link")
      navigate("/login", { replace: true })
    } finally {
      setVerifying(false)
    }
  }, [code, hashParams, navigate])

  useEffect(() => {
    if (!tokenHash) performVerification()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!verified) return

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.")
      return
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.")
      return
    }

    try {
      setSubmitting(true)

      if (tokenHash && !tokenHashVerified.current) {
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: otpType,
        })
        if (verifyError) {
          console.error("Link verification failed:", verifyError)
          toast.error("This link is invalid or has expired.", {
            description: isRecovery
              ? "Request a new password reset link."
              : "Ask an admin to send you a new link.",
          })
          navigate(isRecovery ? "/forgot-password" : "/login", { replace: true })
          return
        }
        tokenHashVerified.current = true
      }

      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error

      if (isRecovery) {
        // A reset is often a response to a suspected compromise, so end every other session. Not
        // fatal if it fails: the password has already changed.
        const { error: signOutError } = await supabase.auth.signOut({ scope: "others" })
        if (signOutError) console.error("Failed to sign out other sessions:", signOutError)
      }

      toast.success(
        isRecovery
          ? "Password reset successfully. Redirecting..."
          : "Password set successfully. Redirecting...",
      )
      // After setting password, the session remains valid; send them to their role's home page
      const {
        data: { session },
      } = await supabase.auth.getSession()
      navigate(session ? getDefaultHomePageForAccessToken(session.access_token) : "/", {
        replace: true,
      })
    } catch (err: unknown) {
      console.error("Failed to set password:", err)
      toast.error(getErrorMessage(err) || "Failed to set password")
    } finally {
      setSubmitting(false)
    }
  }

  if (verifying) {
    return (
      <div className="min-h-screen bg-palette-jungleGreen000 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-palette-jungleGreen300 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-palette-jungleGreen600">
            {isRecovery ? "Verifying your reset password link…" : "Verifying your invite link…"}
          </p>
        </div>
      </div>
    )
  }

  if (!verified) {
    return null
  }

  return (
    <div className="min-h-screen bg-palette-jungleGreen000 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          alt="DriverHive"
          className="mx-auto h-12"
          src="/images/b324345a-d7f5-4435-8a45-ce025baae0be.png"
        />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-palette-jungleGreen900">
          {isRecovery ? "Reset your password" : "Set your password"}
        </h2>
        {email && (
          <p className="mt-2 text-center text-sm text-palette-jungleGreen600">Account: {email}</p>
        )}
        <p className="mt-4 text-center text-sm text-palette-jungleGreen500">
          {isRecovery
            ? "Please enter a new password to reset your account password. Make sure it's at least 8 characters long."
            : "Please set a password to activate your account. Make sure it's at least 8 characters long."}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={onSubmit}>
            <div>
              <label
                htmlFor="new-password"
                className="block text-sm font-medium text-palette-jungleGreen700"
              >
                {isRecovery ? "New password" : "Password"}
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="lock" className="h-5 w-5 text-palette-jungleGreen400" />
                </div>
                <Input
                  id="new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder={isRecovery ? "Enter your new password" : "Enter a strong password"}
                  className="pl-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-palette-jungleGreen700"
              >
                {isRecovery ? "Confirm new password" : "Confirm password"}
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="lock" className="h-5 w-5 text-palette-jungleGreen400" />
                </div>
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder={isRecovery ? "Re-enter your new password" : "Re-enter password"}
                  className="pl-10"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Saving..." : isRecovery ? "Reset password" : "Set password"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ActivateUser
